import { useEffect, useState } from "react";
import { individualPersonService } from "../../Api/Services/individualPersonService";
import { accountService } from "../../Api/Services/accountService";
import { IndividualPerson } from "../../Models/IndividualPerson";
import { Gender } from "../../Enums/Gender";
import { formatCpf, formatPhoneNumber, unformat } from "../../Utils/DataFormatters";
import { Input } from "../Input/Input";
import { Select } from "../Select/Select";
import { toast } from "react-toastify";
import Button from "../Button/Button";

type IndividualPersonFormProps = {
  individualPerson: IndividualPerson;
  email: string;
  phoneNumber: string;
  onSave: () => void;
};

const IndividualPersonForm = ({ individualPerson, email, phoneNumber, onSave }: IndividualPersonFormProps) => {
  const [editedFullName, setEditedFullName] = useState<string | null>(null);
  const [editedPhoneNumber, setEditedPhoneNumber] = useState<string | null>(null);
  const [editedGender, setEditedGender] = useState<Gender | null>(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const hasChanges = editedFullName !== null || editedPhoneNumber !== null || editedGender !== null;
    setIsButtonEnabled(hasChanges);
  }, [editedFullName, editedPhoneNumber, editedGender]);

  const handleSubmit = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    let updatedIndividualPerson: boolean = false;
    let updatedPhoneNumber: boolean = false;

    try {
      if (editedFullName !== null || editedGender !== null) {
        updatedIndividualPerson = await individualPersonService.update({
          id: individualPerson.id,
          fullName: editedFullName, 
          gender: editedGender
        });
      }

      if (editedPhoneNumber !== null) {

        try {
          const updated = await accountService.updatePhoneNumber({
            phoneNumber: editedPhoneNumber
          });
          if (!updated) {
            toast.error("Erro ao atualizar telefone.");
            return;
          }
          updatedPhoneNumber = true;
          const formattedPhone = formatPhoneNumber(editedPhoneNumber);
          setEditedPhoneNumber(formattedPhone);
        } catch (err: any) {
          const message = err?.response?.data as string | undefined;
          toast.error(message ?? "Erro ao atualizar telefone.");
          return;
        }
      }

      if (updatedIndividualPerson || updatedPhoneNumber) {
        onSave();
        toast.success("Alterações salvas com sucesso!");

        setEditedFullName(null);
        setEditedPhoneNumber(null);
        setEditedGender(null);
        setIsButtonEnabled(false);
      }
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <div className="flex flex-col gap-3">
      <form className="space-y-4">
        <Input
          label={"Nome completo"}
          type="text"
          name="fullName"
          value={editedFullName ?? individualPerson.fullName}
          onChange={e => setEditedFullName(e.target.value)}
          disabled={isSaving}
          required
        />

        <Input
          label={"Telefone celular"}
          type="tel"
          name="phoneNumber"
          value={formatPhoneNumber(editedPhoneNumber ?? phoneNumber)}
          onChange={e => setEditedPhoneNumber(unformat(e.target.value))}
          disabled={isSaving}
          required
        />

        <Select
          label={"Gênero"}
          name="gender"
          value={editedGender ?? individualPerson.gender ?? ""}
          onChange={e => setEditedGender(e.target.value as unknown as Gender)}
          disabled={isSaving}
          required
          options={[
            { value: Gender.Male, label: "Masculino" },
            { value: Gender.Female, label: "Feminino" },
            { value: Gender.Other, label: "Outro" },
            { value: Gender.PreferNotToSay, label: "Prefiro não informar" }
          ]}
        />

        <Input
          label={"E-mail"}
          type="email"
          name="email"
          value={email}
          readOnly
        />

        <div className="flex gap-2">
          <div className="flex-1 min-w-0">
            <Input
              label={"CPF"}
              type="text"
              name="cpf"
              value={formatCpf(individualPerson.cpf)}
              readOnly
            />
          </div>
          <div className="flex-1 min-w-0">
            <Input
              label={"Data de nascimento"}
              type="date"
              name="birthDate"
              value={individualPerson.birthDate}
              readOnly
            />
          </div>
        </div>
      </form>
      <p className="text-xs text-gray-500">{"(*) Campos obrigatórios."}</p>
      <Button isLoading={isSaving}
        disabled={!isButtonEnabled || isSaving}
        className="w-full py-2"
        onClick={handleSubmit}
      >
        {"Salvar alterações"}
      </Button>
    </div>           
  );
};

export default IndividualPersonForm;
